import React, { useState, useEffect } from 'react';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const BeneficiarySetupStep = ({ data, personalDetails, onComplete, onBack, onCancel }) => {
  const [beneficiaries, setBeneficiaries] = useState(data?.length > 0 ? data : []);
  const [errors, setErrors] = useState({});
  const [totalAllocation, setTotalAllocation] = useState(0);

  const relationshipOptions = [
    { value: 'spouse', label: 'Spouse' },
    { value: 'child', label: 'Child' },
    { value: 'parent', label: 'Parent' },
    { value: 'sibling', label: 'Sibling' },
    { value: 'other_relative', label: 'Other Relative' },
    { value: 'other', label: 'Other' }
  ];

  // Calculate total allocation percentage
  useEffect(() => {
    const total = beneficiaries?.reduce((sum, b) => {
      const percentage = parseFloat(b?.percentage) || 0;
      return sum + percentage;
    }, 0);
    setTotalAllocation(total);
  }, [beneficiaries]);

  const handleAddBeneficiary = () => {
    setBeneficiaries(prev => [
      ...prev,
      {
        id: Date.now(),
        fullName: '',
        relationship: '',
        dateOfBirth: '',
        contactNumber: '',
        percentage: ''
      }
    ]);
  };

  const handleRemoveBeneficiary = (id) => {
    setBeneficiaries(prev => prev?.filter(b => b?.id !== id));
  };

  const handleBeneficiaryChange = (id, field, value) => {
    setBeneficiaries(prev =>
      prev?.map(b => (b?.id === id ? { ...b, [field]: value } : b))
    );
    if (errors?.[`beneficiary_${id}_${field}`]) {
      setErrors(prev => ({ ...prev, [`beneficiary_${id}_${field}`]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (beneficiaries?.length === 0) {
      newErrors.general = 'At least one beneficiary is required';
      setErrors(newErrors);
      return false;
    }

    beneficiaries?.forEach((beneficiary) => {
      if (!beneficiary?.fullName?.trim()) {
        newErrors[`beneficiary_${beneficiary?.id}_fullName`] = 'Name is required';
      }

      if (!beneficiary?.relationship) {
        newErrors[`beneficiary_${beneficiary?.id}_relationship`] = 'Relationship is required';
      }

      if (!beneficiary?.dateOfBirth) {
        newErrors[`beneficiary_${beneficiary?.id}_dateOfBirth`] = 'Date of birth is required';
      }

      if (!beneficiary?.percentage) {
        newErrors[`beneficiary_${beneficiary?.id}_percentage`] = 'Percentage is required';
      } else {
        const percentage = parseFloat(beneficiary?.percentage);
        if (isNaN(percentage) || percentage <= 0 || percentage > 100) {
          newErrors[`beneficiary_${beneficiary?.id}_percentage`] = 'Enter valid percentage (1-100)';
        }
      }
    });

    if (totalAllocation !== 100) {
      newErrors.allocation = `Total allocation must equal 100% (currently ${totalAllocation?.toFixed(1)}%)`;
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (validateForm()) {
      onComplete?.(beneficiaries);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-lg p-8">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Beneficiary Setup</h2>
        <p className="text-gray-600">
          Add beneficiaries for {personalDetails?.fullName || 'employee'}'s NASFUND account
        </p>
      </div>
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Beneficiaries List */}
        {beneficiaries?.map((beneficiary, index) => (
          <div key={beneficiary?.id} className="border border-gray-200 rounded-lg p-6 relative">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-900">
                Beneficiary {index + 1}
              </h3>
              {beneficiaries?.length > 1 && (
                <button
                  type="button"
                  onClick={() => handleRemoveBeneficiary(beneficiary?.id)}
                  className="text-red-600 hover:text-red-700 transition-colors"
                >
                  <Icon name="Trash2" size={18} />
                </button>
              )}
            </div>

            <div className="space-y-4">
              {/* Full Name */}
              <Input
                label="Full Name"
                type="text"
                required
                value={beneficiary?.fullName}
                onChange={(e) => handleBeneficiaryChange(beneficiary?.id, 'fullName', e?.target?.value)}
                error={errors?.[`beneficiary_${beneficiary?.id}_fullName`]}
                placeholder="Beneficiary full name"
              />

              {/* Relationship and Date of Birth */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Select
                  label="Relationship"
                  required
                  options={relationshipOptions}
                  value={beneficiary?.relationship}
                  onChange={(value) => handleBeneficiaryChange(beneficiary?.id, 'relationship', value)}
                  error={errors?.[`beneficiary_${beneficiary?.id}_relationship`]}
                  placeholder="Select relationship"
                />

                <Input
                  label="Date of Birth"
                  type="date"
                  required
                  value={beneficiary?.dateOfBirth}
                  onChange={(e) => handleBeneficiaryChange(beneficiary?.id, 'dateOfBirth', e?.target?.value)}
                  error={errors?.[`beneficiary_${beneficiary?.id}_dateOfBirth`]}
                />
              </div>

              {/* Contact Number and Percentage */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Contact Number (Optional)"
                  type="tel"
                  value={beneficiary?.contactNumber}
                  onChange={(e) => handleBeneficiaryChange(beneficiary?.id, 'contactNumber', e?.target?.value)}
                  placeholder="+675 7123 4567"
                />

                <div>
                  <Input
                    label="Allocation Percentage"
                    type="number"
                    step="0.1"
                    min="0"
                    max="100"
                    required
                    value={beneficiary?.percentage}
                    onChange={(e) => handleBeneficiaryChange(beneficiary?.id, 'percentage', e?.target?.value)}
                    error={errors?.[`beneficiary_${beneficiary?.id}_percentage`]}
                    placeholder="0.0"
                  />
                  <p className="text-xs text-gray-500 mt-1">Enter percentage (0-100)</p>
                </div>
              </div>
            </div>
          </div>
        ))}

        {/* Add Beneficiary Button */}
        <Button
          type="button"
          variant="outline"
          onClick={handleAddBeneficiary}
          className="w-full"
        >
          <Icon name="Plus" size={16} className="mr-2" />
          Add Another Beneficiary
        </Button>

        {/* General Error */}
        {errors?.general && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-sm text-red-700">{errors?.general}</p>
          </div>
        )}

        {/* Allocation Summary */}
        <div className={`rounded-lg p-6 ${
          totalAllocation === 100
            ? 'bg-emerald-50 border border-emerald-200'
            : totalAllocation > 100
            ? 'bg-red-50 border border-red-200' :'bg-amber-50 border border-amber-200'
        }`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="font-semibold text-gray-900 mb-1">Total Allocation</p>
              <p className="text-sm text-gray-600">
                {beneficiaries?.length} beneficiar{beneficiaries?.length === 1 ? 'y' : 'ies'} added
              </p>
            </div>
            <div className="text-right">
              <p className={`text-3xl font-bold ${
                totalAllocation === 100
                  ? 'text-emerald-600'
                  : totalAllocation > 100
                  ? 'text-red-600' :'text-amber-600'
              }`}>
                {totalAllocation?.toFixed(1)}%
              </p>
              {totalAllocation === 100 ? (
                <div className="flex items-center text-emerald-600 text-sm mt-1">
                  <Icon name="CheckCircle" size={14} className="mr-1" />
                  Complete
                </div>
              ) : (
                <p className="text-sm text-gray-600 mt-1">
                  {totalAllocation < 100 ? 'Remaining' : 'Over by'}: {Math.abs(100 - totalAllocation)?.toFixed(1)}%
                </p>
              )}
            </div>
          </div>
          {errors?.allocation && (
            <p className="text-sm text-red-700 mt-3">
              <Icon name="AlertCircle" size={14} className="inline mr-1" />
              {errors?.allocation}
            </p>
          )}
        </div>

        {/* Information Banner */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start">
            <Icon name="Info" size={20} className="text-blue-600 mr-3 mt-0.5" />
            <div>
              <p className="font-semibold text-blue-900 mb-1">Beneficiary Requirements</p>
              <p className="text-sm text-blue-700">
                Total allocation must equal 100%. You can add multiple beneficiaries and adjust percentages. 
                Beneficiaries will receive NASFUND benefits according to their allocated percentage.
              </p>
            </div>
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex gap-3 pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={onBack}
          >
            <Icon name="ArrowLeft" size={16} className="mr-2" />
            Back
          </Button>
          <Button
            type="submit"
            variant="primary"
            className="flex-1"
            disabled={totalAllocation !== 100}
          >
            Continue to Document Upload
            <Icon name="ArrowRight" size={16} className="ml-2" />
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
          >
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
};

export default BeneficiarySetupStep;